import _ from "lodash";
import React from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { connect } from "react-redux";

import { Button } from "@toddle-edu/ds-web";

import { highlightTextWithCustomStyle } from "Utils";
import { useI18n } from "Hooks";
import ConditionalTooltipText from "UIComponents/ConditionalTooltipText";
import { STUDENT } from "Constants/stringConstants";
import {
  getUserType,
  getDefaultNameSortPreference,
  getDefaultStudentNameOrderPreference,
} from "modules/CommonSelectors";
import { onEnterOrSpaceKeyDown } from "Utils/AccessibilityHelpers";
import classes from "./UserTypeList.scss";
import SkeletonLoader from "UIComponents/SkeletonLoader";
import ProfileCascade from "UIComponents/ProfileCascade";
import StudentAlerts from "AppComponents/StudentAlerts";
import {
  getFormattedNameForUserType,
  getProfileItem,
  getSubText,
  isMessagingRevempEnabled,
} from "MessagingV2/modules/Utils";

const UserCardSkeleton = () => {
  return _.times(4, i => (
    <div key={i} className={classes.selectionCardContainerSkeleton}>
      <div className={classes.userAvatar}>
        <SkeletonLoader variant="rounded" width={40} height={40} />
      </div>
      <div className={classes.skeletonUserInfoContainer}>
        <SkeletonLoader width={220} height={14} />
        <SkeletonLoader width={100} height={12} />
      </div>
    </div>
  ));
};

const HighlightedText = props => {
  const { text, highlightText, ...rest } = props;
  const highlightedItem = text
    ? highlightTextWithCustomStyle(text, highlightText)
    : "";

  return (
    <div
      {...rest}
      dangerouslySetInnerHTML={{
        __html: isMessagingRevempEnabled() ? text : highlightedItem,
      }}
    />
  );
};

const getAvatarStyles = ({ customStyles = {} }) => {
  return {
    size: "medium",
    shade: "light",
    randomizeColor: true,
    shape: "square",
    ...customStyles,
  };
};

const UserSelectionCard = props => {
  const {
    user = {},
    searchText,
    onUserClick,
    defaultNameSortPreference,
    defaultStudentNameOrderPreference,
    currentUserRole,
    overrideClasses = {},
    customStyles = {},
  } = props;
  const { id, isArchived, type, alertTypes = [] } = user;

  const { t } = useI18n(["common"]);

  const userFormattedName = getFormattedNameForUserType({
    user,
    orderBy: defaultNameSortPreference,
    defaultStudentNameOrderPreference,
    isFormattedNameV2: true,
  });

  const userSubTitleText = getSubText({
    loggedInUserRole: currentUserRole,
    messagedUserRole: type,
    messagedUserObj: user,
    t,
  });

  const handleUserClick = () => onUserClick(user);
  const handleUserCardKeyDown = e => onEnterOrSpaceKeyDown(e, handleUserClick);
  const profileItem = getProfileItem({
    user,
    defaultNameSortPreference,
    defaultStudentNameOrderPreference,
  });

  const userSubTitle = classNames(
    classes.userSubTitle,
    "text-body-s",
    overrideClasses.userSubTitle
  );
  const userTitleClass = classNames(
    classes.userTitle,
    "text-body",
    overrideClasses.userTitle
  );

  return (
    <div
      className={classes.selectionCardContainerV2}
      role="button"
      tabIndex={0}
      onClick={handleUserClick}
      onKeyDown={handleUserCardKeyDown}
    >
      <div className={classes.userAvatar}>
        <ProfileCascade
          items={[profileItem]}
          shouldDynamicColor
          showAvatar
          avatarStyles={getAvatarStyles({ customStyles })}
          isArchived={isArchived}
          dsVersion="2.0"
        />
      </div>
      <div className={classes.userInfoContainer}>
        <div className={classes.userNameContainer}>
          <HighlightedText
            className={userTitleClass}
            text={userFormattedName}
            highlightText={searchText}
          />
          {type === STUDENT ? (
            <StudentAlerts studentId={id} alertTypes={alertTypes} />
          ) : null}
        </div>
        {userSubTitleText ? (
          <ConditionalTooltipText
            text={userSubTitleText}
            className={userSubTitle}
          />
        ) : null}
      </div>
    </div>
  );
};

const UserTypeList = props => {
  const {
    searchText,
    headerTitle,
    onUserClick,
    isFetchingMore,
    hasNextPage,
    loadMoreUsers,
    userNodeList,
    userTypeListContainerClassName,
    currentUserRole,
    overrideClasses = {},
    defaultNameSortPreference,
    defaultStudentNameOrderPreference,
    footerStyle = {},
    customStyles = {},
  } = props;

  const { t } = useI18n(["common"]);
  const isUIRevempEnabled = isMessagingRevempEnabled();
  const headerContainerV2 = classNames(
    classes.headerContainerV2,
    "text-label-3"
  );

  const containerClasses = classNames(
    classes.container,
    userTypeListContainerClassName
  );

  return (
    <div className={containerClasses}>
      <div className={headerContainerV2}>{headerTitle}</div>
      {_.map(userNodeList, userNode => {
        const user = _.get(userNode, "node");
        if (!user) return null;
        return (
          <UserSelectionCard
            key={user.id}
            user={user}
            onUserClick={onUserClick}
            searchText={searchText}
            currentUserRole={currentUserRole}
            overrideClasses={overrideClasses}
            defaultNameSortPreference={defaultNameSortPreference}
            defaultStudentNameOrderPreference={
              defaultStudentNameOrderPreference
            }
            customStyles={customStyles}
          />
        );
      })}

      {isFetchingMore ? (
        <UserCardSkeleton />
      ) : hasNextPage ? (
        <div
          className={classNames(overrideClasses.footerContainer, {
            [classes.footerContainerV2]: true,
          })}
        >
          <Button
            onClick={loadMoreUsers}
            variant="progressive"
            type="inline"
            dsVersion="2.0"
            size="small"
          >
            <div
              className={classNames(
                "text-label-s",
                overrideClasses.showMoreButton
              )}
            >
              {t("common:show_more")}
            </div>
          </Button>
        </div>
      ) : null}
    </div>
  );
};

UserTypeList.propTypes = {
  searchText: PropTypes.string.isRequired,
  onUserClick: PropTypes.func.isRequired,
  headerTitle: PropTypes.string.isRequired,
  isFetchingMore: PropTypes.bool.isRequired,
  hasNextPage: PropTypes.bool.isRequired,
  loadMoreUsers: PropTypes.func.isRequired,
  userNodeList: PropTypes.arrayOf(PropTypes.object).isRequired,
};

const mapStateToProps = state => {
  return {
    currentUserRole: getUserType(state),
    defaultNameSortPreference: getDefaultNameSortPreference(state),
    defaultStudentNameOrderPreference: getDefaultStudentNameOrderPreference(
      state
    ),
  };
};
export default connect(mapStateToProps)(UserTypeList);
